import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { Category, SpendingMode } from '../api/types';
import { CategoryMark } from '../components/CategoryMark';
import { EyeIcon, EyeOffIcon, PencilIcon, PlusIcon } from '../components/icons';
import { haptic } from '../lib/telegram';
import { humanError } from '../lib/errors';
import { useToast } from '../lib/toast';
import { useMainButton } from '../lib/use-main-button';

type EditState = null | { mode: 'create' } | { mode: 'edit'; cat: Category };

export function Categories(): JSX.Element {
  const [tab, setTab] = useState<'expense' | 'income'>('expense');
  const [showHidden, setShowHidden] = useState(false);
  const [edit, setEdit] = useState<EditState>(null);

  const categoriesQ = useQuery({
    queryKey: ['categories', tab, showHidden],
    queryFn: () => api.categories(tab, showHidden),
  });

  useMainButton({
    text: '+ Добавить категорию',
    onClick: () => setEdit({ mode: 'create' }),
    visible: !edit,
  });

  return (
    <div className="screen">
      <div className="list-toolbar">
        <h2 className="screen-title">Категории</h2>
        <button type="button" className="link-btn" onClick={() => setShowHidden((v) => !v)}>
          {showHidden ? 'Без скрытых' : 'Все'}
        </button>
      </div>

      {/* Видимая кнопка добавления. Раньше создание висело ТОЛЬКО на
          Telegram MainButton: он прижат к низу окна Telegram, его легко не
          заметить, а вне Telegram его нет вовсе — и добавить категорию было
          неоткуда. MainButton оставлен как второй путь. */}
      <button type="button" className="cat-add" onClick={() => setEdit({ mode: 'create' })}>
        <PlusIcon size={16} />
        Новая категория
      </button>

      <div className="kind-toggle">
        <button
          type="button"
          className={`kind-btn ${tab === 'expense' ? 'active' : ''}`}
          onClick={() => setTab('expense')}
        >
          Расходы
        </button>
        <button
          type="button"
          className={`kind-btn ${tab === 'income' ? 'active' : ''}`}
          onClick={() => setTab('income')}
        >
          Доходы
        </button>
      </div>

      {categoriesQ.isLoading ? <div className="loading">Загружаем…</div> : null}
      {categoriesQ.error ? (
        <div className="error">{humanError(categoriesQ.error, 'Категории не загрузились')}</div>
      ) : null}

      <div className="category-list">
        {(categoriesQ.data ?? []).map((c) => (
          <CategoryRow key={c.id} cat={c} onEdit={() => setEdit({ mode: 'edit', cat: c })} />
        ))}
      </div>

      {edit ? (
        <CategoryFormSheet
          mode={edit.mode}
          initial={edit.mode === 'edit' ? edit.cat : undefined}
          defaultKind={tab}
          onClose={() => setEdit(null)}
        />
      ) : null}
    </div>
  );
}

function CategoryRow({
  cat,
  onEdit,
}: {
  cat: Category;
  onEdit: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [err, setErr] = useState<string | null>(null);
  const visMut = useMutation({
    mutationFn: (isHidden: boolean) => api.setCategoryHidden(cat.id, isHidden),
    onSuccess: () => {
      haptic('light');
      setErr(null);
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
    onError: () => {
      haptic('error');
      setErr('Не удалось изменить видимость');
    },
  });
  // Разовый платёж или размазанный по месяцу. Решает, экстраполируется ли расход
  // по дням: подписки, оплаченные в день зарплаты, — это план, сработавший как
  // надо, а не двукратный перерасход.
  const modeMut = useMutation({
    mutationFn: (mode: SpendingMode) => api.updateCategory(cat.id, { spending_mode: mode }),
    onSuccess: () => {
      haptic('light');
      setErr(null);
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      // Прогнозы и статусы бюджета считаются из этого поля — иначе экран
      // остался бы со старыми числами до перезагрузки.
      queryClient.invalidateQueries({ queryKey: ['budget'] });
    },
    onError: () => {
      haptic('error');
      setErr('Не удалось переключить режим');
    },
  });
  const isLump = cat.spending_mode === 'lump';
  return (
    <div className={`cat-row ${cat.is_hidden ? 'hidden' : ''}`}>
      <CategoryMark name={cat.name} slug={cat.slug} />
      <div className="cat-main">
        <div className="cat-name">{cat.name}</div>
        <div className="cat-sub">
          {cat.is_default ? 'по умолчанию, ' : ''}
          {cat.slug}
          {cat.is_hidden ? ' · скрыта' : ''}
        </div>
        {cat.kind === 'expense' ? (
          <button
            type="button"
            className={`mode-pill ${isLump ? 'lump' : 'spread'}`}
            disabled={modeMut.isPending}
            onClick={() => modeMut.mutate(isLump ? 'spread' : 'lump')}
            title={
              isLump
                ? 'Платится разом: темп по дням не считается'
                : 'Траты идут весь месяц: прогноз по темпу'
            }
          >
            {isLump ? 'разовый платёж' : 'весь месяц'}
          </button>
        ) : null}
        {err ? <div className="cat-err">{err}</div> : null}
      </div>
      <div className="cat-actions">
        <button type="button" className="icon-btn" onClick={onEdit} aria-label="Редактировать">
          <PencilIcon size={18} />
        </button>
        <button
          type="button"
          className="icon-btn"
          disabled={visMut.isPending}
          onClick={() => visMut.mutate(!cat.is_hidden)}
          aria-label={cat.is_hidden ? 'Показать' : 'Скрыть'}
        >
          {cat.is_hidden ? <EyeIcon size={18} /> : <EyeOffIcon size={18} />}
        </button>
      </div>
    </div>
  );
}

function CategoryFormSheet({
  mode,
  initial,
  defaultKind,
  onClose,
}: {
  mode: 'create' | 'edit';
  initial?: Category;
  defaultKind: 'expense' | 'income';
  onClose: () => void;
}): JSX.Element {
  const [name, setName] = useState(initial?.name ?? '');
  const [emoji, setEmoji] = useState(initial?.emoji ?? '');
  const [kind, setKind] = useState<'expense' | 'income'>(initial?.kind ?? defaultKind);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const queryClient = useQueryClient();
  const toast = useToast();
  const createMut = useMutation({
    mutationFn: () =>
      api.createCategory({
        name: name.trim(),
        kind,
        emoji: emoji || undefined,
      }),
    onSuccess: () => {
      haptic('success');
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      onClose();
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось создать категорию')),
  });
  const updateMut = useMutation({
    mutationFn: () => {
      if (!initial) throw new Error('No initial');
      return api.updateCategory(initial.id, {
        name: name.trim(),
        emoji: emoji || null,
      });
    },
    onSuccess: () => {
      haptic('success');
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      onClose();
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось сохранить категорию')),
  });

  const isPending = createMut.isPending || updateMut.isPending;
  const isValid = name.trim().length > 0;

  useMainButton({
    text: mode === 'create' ? 'Создать' : 'Сохранить',
    onClick: () => {
      if (!isValid) return;
      if (mode === 'create') createMut.mutate();
      else updateMut.mutate();
    },
    enabled: isValid && !isPending,
    progress: isPending,
  });

  return (
    <div className="sheet-backdrop" onClick={onClose} role="presentation">
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="sheet-handle" />
        <div className="sheet-title">
          {mode === 'create' ? 'Новая категория' : 'Редактировать категорию'}
        </div>

        {mode === 'create' ? (
          <div className="kind-toggle">
            <button
              type="button"
              className={`kind-btn ${kind === 'expense' ? 'active' : ''}`}
              onClick={() => setKind('expense')}
            >
              Расход
            </button>
            <button
              type="button"
              className={`kind-btn ${kind === 'income' ? 'active' : ''}`}
              onClick={() => setKind('income')}
            >
              Доход
            </button>
          </div>
        ) : null}

        <label className="form-field">
          <span className="form-label">Название</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="form-input"
            autoFocus
            placeholder="Например: Кофе"
          />
          <span className="form-hint">
            В приложении категория отмечается первой буквой названия.
          </span>
        </label>

        <label className="form-field">
          <span className="form-label">Значок для бота</span>
          <input
            type="text"
            value={emoji}
            onChange={(e) => setEmoji(e.target.value.slice(0, 4))}
            className="form-input"
          />
          <span className="form-hint">Показывается в сообщениях бота, не в приложении.</span>
        </label>

        <div className="sheet-actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={isPending}>
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}
